import "./index.css";
// Bootstrap axios interceptors (auth header injection, 401 refresh) before any requests fire
import "./services/axios";
import { Provider } from "react-redux";
import configureStore from "./store/configureStore";
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import { BrowserRouter } from "react-router-dom";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { SnackbarProvider } from "notistack";
import { AxiosError } from "axios";
import { createRoot } from "react-dom/client";
import auth from "shared/Auth0";
// Ensure Font Awesome library is registered once for string icon names
import "shared/icons/fontawesome";
import { FeatureFlagsProvider } from "shared/context/FeatureFlags";
import { SidebarStateProvider } from "shared/context/SidebarState";
import FeatureFlagDevToolbar from "components/DevToolbar/FeatureFlagDevToolbar";
import { TooltipProvider } from "components/ui/tooltip";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      const typedError = error as unknown as AxiosError;
      if (typedError.response?.status === 401) {
        auth.logout();
      }
    },
  }),
});

// Get the application-wide store instance, prepopulating with state from the server where available.
const initialState = (window as any).initialReduxState;
const store: any = configureStore(initialState);

const rootElement = document.getElementById("root");

if (rootElement) {
  const root = createRoot(rootElement);
  root.render(
    <FeatureFlagsProvider>
      <SidebarStateProvider>
        <Provider store={store}>
          <SnackbarProvider
            autoHideDuration={4000}
            maxSnack={2}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
          >
            <BrowserRouter>
              <QueryClientProvider client={queryClient}>
                <TooltipProvider>
                  <App />
                  <FeatureFlagDevToolbar />
                </TooltipProvider>
              </QueryClientProvider>
            </BrowserRouter>
          </SnackbarProvider>
        </Provider>
      </SidebarStateProvider>
    </FeatureFlagsProvider>,
  );
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
